/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetchSignalsAll } from "../../api";
import SignalFilter from "./SignalFilters";

const iconMap = {
  "long_straddle_vol_breakout": "⚡",
  "breakout_pullback": "📈",
  "oi_volume_divergence": "📊",
  "trend_reversal": "🔄",
  "mean_reversion": "↩️",
  default: "📘"
};

const signalDescriptionMap = {
  long_straddle_vol_breakout: "Tracks volatility breakout using long straddle logic.",
  oi_volume_divergence: "Detects divergence between Open Interest and Volume.",
  breakout_pullback: "Captures breakout + pullback continuation setup.",
  volume_spike_price_confirm: "Identifies volume spikes confirmed by price expansion."
};

const SignalDashboard = () => {
  const navigate = useNavigate();

  const [signalsMeta, setSignalsMeta] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    fetchSignalsAll().then((data) => {
      console.log("API SIGNAL RESULTS --> ", data);

      const meta = data.map((item) => ({
        key: item.signal_key,
        name: item.display_name || item.signal_key.replace(/_/g, " ").toUpperCase(),
        description: signalDescriptionMap[item.signal_key] || "Technical signal.",
        signal_id: item.signal_id,
        latest_run: item.latest_run,
        tags: ["options", "technical"]
      }));

      setSignalsMeta(meta);
    });
  }, []);

  const filtered = selectedTag
    ? signalsMeta.filter((s) => s.tags.includes(selectedTag))
    : signalsMeta;

  return (
    <div style={styles.container}>
      <SignalFilter onTagSelect={setSelectedTag} />

      <div style={styles.grid}>
        {filtered.map((sig) => (
          <div
            key={sig.key}
            style={styles.card}
            onClick={() => navigate(`/ssi/${sig.key}`)}
          >
            <h3>
              {iconMap[sig.key] || iconMap.default} {sig.name}
            </h3>

            <p style={styles.desc}>{sig.description}</p>

            {/* Latest Run Info */}
            {sig.latest_run ? (
              <div style={styles.runBox}>
                <div><b>Last Run:</b> {new Date(sig.latest_run.run_ts).toLocaleString()}</div>
                <div><b>Status:</b> {sig.latest_run.status}</div>
                <div><b>Rows:</b> {sig.latest_run.valid_row_count}</div>
              </div>
            ) : (
              <div style={styles.noRun}>No runs yet</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: { display: "flex" },
  grid: {
    flex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "20px",
    padding: "20px"
  },
  card: {
    border: "1px solid #ccc",
    padding: "16px",
    borderRadius: "8px",
    background: "#fafafa",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "0.2s",
  },
  desc: { fontSize: "14px", marginBottom: "8px", opacity: 0.8 },
  runBox: {
    background: "#fff",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "13px",
    marginTop: "10px"
  },
  noRun: {
    fontSize: "13px",
    fontStyle: "italic",
    marginTop: "10px",
    color: "#777"
  }
};

export default SignalDashboard;
